import { type PropsWithChildren, useMemo } from 'react';
import {
  type DefaultTheme,
  ThemeProvider as SCThemeProvider
} from 'styled-components';
import {
  getColorWithAlpha,
  getInvertedColor,
  isDarkColor
} from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { parseBackgroundValue } from '../../utils/parse-background-value';

const CONTROL_COLOR_LIGHT = 'rgb(217, 217, 217)';
const CONTROL_COLOR_DARK = 'rgb(88, 88, 88)';

interface Theme {
  floatingButton: {
    backgroundColor: string;
    outlineColor: string;
    iconColor: string;
    badgeColor: string;
  };
  page: {
    background: string | undefined;
    separatorColor: string;
  };
  header: {
    avatarBackgroundColor: string;
    titleColor: string;
    buttonsColor: string;
    buttonsBackgroundColor: string;
    buttonsOutlineColor: string;
  };
  block: {
    backgroundColor: string;
  };
  modeBlock: {
    separatorColor: string;
  };
  modeItem: {
    activeBackgroundColor: string;
    backgroundColor: string;
    hoverBackgroundColor: string;
    activeHoverBackgroundColor: string;
    outlineColor: string;
    iconColor: string;
    activeIconColor: string;
    labelColor: string;
    activeLabelColor: string;
    mobile: {
      backgroundColor: string;
      activeBackgroundColor: string;
      hoverBackgroundColor: string;
      activeHoverBackgroundColor: string;
    };
  };
  modeInfoIcon: {
    color: string;
    activeColor: string;
    popupBackgroundColor: string;
    popupTextColor: string;
    popupButtonBackgroundColor: string;
    popupButtonColor: string;
  };
  moreSettings: {
    backgroundColor: string;
    hoverBackgroundColor: string;
    outlineColor: string;
    iconColor: string;
    labelColor: string;
    badgeColor: string;
    arrowColor: string;
    mobile: {
      backgroundColor: string;
      hoverBackgroundColor: string;
    };
  };
  scoreBlock: {
    iconColor: string;
    textColor: string;
  };
  settingBlock: {
    titleColor: string;
  };
  settingItem: {
    backgroundColor: string;
    hoverBackgroundColor: string;
    outlineColor: string;
    iconColor: string;
    labelColor: string;
  };
  toggleSwitchControl: {
    dotColor: string;
    backgroundColor: string;
    activeBackgroundColor: string;
  };
  radioControl: {
    dotColor: string;
    backgroundColor: string;
    activeBackgroundColor: string;
    borderColor: string;
  };
  freeLink: {
    textColor: string;
    borderColor: string;
  };
}

declare module 'styled-components' {
  export interface DefaultTheme extends Theme {
    type: string;
    accentColor: string;
  }
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const { theme } = useSettings();

  const freeLinkStyle: DefaultTheme['freeLink'] = useMemo(() => {
    const background = theme.themeSettings.background;

    const backgroundValue =
      background.type === 'gradient'
        ? background.gradient!.colors[0]
        : background.color;

    const textColor = getInvertedColor(backgroundValue!, [
      'rgba(255, 255, 255, 0.9)',
      'rgba(17, 17, 17, 0.9)'
    ]);

    const borderColor = getInvertedColor(backgroundValue!, [
      'rgba(255, 255, 255, 0.1)',
      'rgba(17, 17, 17, 0.1)'
    ]);

    return {
      textColor,
      borderColor
    };
  }, [theme.themeSettings.background]);

  const pageBackground = parseBackgroundValue(theme.themeSettings.background);

  const themeValue: DefaultTheme = useMemo(() => {
    const modeItem: Theme['modeItem'] = {
      backgroundColor: 'transparent',
      activeBackgroundColor: theme.themeSettings.settingsAccentColor,
      hoverBackgroundColor: getColorWithAlpha(
        theme.themeSettings.settingsAccentColor,
        0.1
      ),
      activeHoverBackgroundColor: `color-mix(in srgb, ${theme.themeSettings.settingsAccentColor} 90%, ${getInvertedColor(
        theme.themeSettings.settingsAccentColor
      )} 10%)`,
      outlineColor: theme.themeSettings.settingsAccentColor,
      iconColor: theme.themeSettings.settingsAccentColor,
      activeIconColor: getInvertedColor(
        theme.themeSettings.settingsAccentColor
      ),
      labelColor: theme.themeSettings.settingsTextColor,
      activeLabelColor: getInvertedColor(
        theme.themeSettings.settingsAccentColor
      ),
      mobile: {
        backgroundColor: theme.themeSettings.settingsBackgroundColor,
        activeBackgroundColor: theme.themeSettings.settingsAccentColor,
        hoverBackgroundColor: `color-mix(in srgb, ${theme.themeSettings.settingsBackgroundColor} 90%, ${theme.themeSettings.settingsAccentColor} 10%)`,
        activeHoverBackgroundColor: `color-mix(in srgb, ${theme.themeSettings.settingsAccentColor} 90%, ${getInvertedColor(
          theme.themeSettings.settingsAccentColor
        )} 10%)`
      }
    };

    return {
      type: theme.type,
      accentColor: theme.accentColor,
      floatingButton: {
        backgroundColor: theme.themeSettings.launcherButtonColor,
        outlineColor: theme.themeSettings.launcherButtonColor,
        iconColor: getInvertedColor(theme.themeSettings.launcherButtonColor),
        badgeColor: 'rgb(255, 255, 255)'
      },
      page: {
        background: pageBackground,
        separatorColor: getInvertedColor(pageBackground!, [
          'rgba(255, 255, 255, 0.1)',
          'rgba(17, 17, 17, 0.1)'
        ])
      },
      header: {
        avatarBackgroundColor: theme.themeSettings.headerIconBackgroundColor,
        titleColor: theme.themeSettings.headerTextColor,
        buttonsColor: theme.themeSettings.headerButtonsColor,
        buttonsBackgroundColor: isDarkColor(
          theme.themeSettings.headerButtonsColor
        )
          ? getColorWithAlpha(theme.themeSettings.headerButtonsColor, 0.05)
          : getColorWithAlpha(theme.themeSettings.headerButtonsColor, 0.1),
        buttonsOutlineColor: theme.themeSettings.headerButtonsColor
      },
      block: {
        backgroundColor: theme.themeSettings.settingsBackgroundColor
      },
      modeBlock: {
        separatorColor: getInvertedColor(
          theme.themeSettings.settingsBackgroundColor,
          ['rgba(255, 255, 255, 0.1)', 'rgba(17, 17, 17, 0.1)']
        )
      },
      modeItem,
      modeInfoIcon: {
        color: theme.themeSettings.settingsAccentColor,
        activeColor: getInvertedColor(theme.themeSettings.settingsAccentColor),
        popupBackgroundColor: theme.themeSettings.settingsBackgroundColor,
        popupTextColor: theme.themeSettings.settingsTextColor,
        popupButtonBackgroundColor: theme.themeSettings.settingsAccentColor,
        popupButtonColor: getInvertedColor(
          theme.themeSettings.settingsAccentColor
        )
      },
      moreSettings: {
        backgroundColor: modeItem.backgroundColor,
        hoverBackgroundColor: modeItem.hoverBackgroundColor,
        outlineColor: modeItem.outlineColor,
        iconColor: modeItem.iconColor,
        labelColor: modeItem.labelColor,
        badgeColor: theme.themeSettings.settingsAccentColor,
        arrowColor: getInvertedColor(
          theme.themeSettings.settingsBackgroundColor,
          ['rgba(255, 255, 255, 0.5)', 'rgba(17, 17, 17, 0.3)']
        ),
        mobile: {
          backgroundColor: modeItem.mobile.backgroundColor,
          hoverBackgroundColor: modeItem.mobile.hoverBackgroundColor
        }
      },
      scoreBlock: {
        iconColor: theme.themeSettings.settingsAccentColor,
        textColor: theme.themeSettings.settingsTextColor
      },
      settingBlock: {
        titleColor: theme.themeSettings.settingsTextColor
      },
      settingItem: {
        backgroundColor: 'transparent',
        hoverBackgroundColor: getColorWithAlpha(
          theme.themeSettings.settingsAccentColor,
          0.1
        ),
        outlineColor: theme.themeSettings.settingsAccentColor,
        iconColor: theme.themeSettings.settingsAccentColor,
        labelColor: theme.themeSettings.settingsTextColor
      },
      toggleSwitchControl: {
        dotColor: 'rgb(255, 255, 255)',
        backgroundColor: isDarkColor(
          theme.themeSettings.settingsBackgroundColor
        )
          ? CONTROL_COLOR_DARK
          : CONTROL_COLOR_LIGHT,
        activeBackgroundColor: theme.themeSettings.settingsAccentColor
      },
      radioControl: {
        dotColor: 'rgb(255, 255, 255)',
        backgroundColor: isDarkColor(
          theme.themeSettings.settingsBackgroundColor
        )
          ? CONTROL_COLOR_DARK
          : CONTROL_COLOR_LIGHT,
        activeBackgroundColor: theme.themeSettings.settingsAccentColor,
        borderColor: isDarkColor(theme.themeSettings.settingsBackgroundColor)
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(17, 17, 17, 0.05)'
      },
      freeLink: freeLinkStyle
    };
  }, [theme]);

  return <SCThemeProvider theme={themeValue}>{children}</SCThemeProvider>;
}
